import { useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { DAYS_IN_WEEK } from '@new/constants';
import {
  useCreateBulkSlotsMutation,
  useDeleteAvailabilitySlotMutation,
} from '@teachur/http-client';
import {
  getStartOfWeek,
  getEndOfWeek,
  getWeekDates,
  createDateFromSlot,
  isSlotBooked,
  isSlotPast,
  getSlotsInRange,
  getSlotStatus,
} from '../../lib/helpers';
import {
  WORK_HOURS_START,
  WORK_HOURS_END,
  SLOT_COLORS,
  SLOT_HOVER_COLORS,
} from '../../lib/constants';
import type { UserAvailabilitySlot, ScheduledCall } from '@teachur/http-client';
import type { TimeSlot } from '../../lib/helpers';
import { cn } from '@new/lib';

interface UseWeeklyCalendarProps {
  availabilitySlots: UserAvailabilitySlot[];
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

const getErrorMessage = (error: Error): string => {
  const data = (error as { response?: { data?: { message?: string } } }).response?.data;
  return data?.message ?? error.message;
};

export const useWeeklyCalendar = ({
  availabilitySlots,
  selectedDate,
  onDateChange,
}: UseWeeklyCalendarProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<TimeSlot | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<TimeSlot[]>([]);
  const [selectedScheduledCall, setSelectedScheduledCall] = useState<ScheduledCall | null>(null);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);

  const createBulkSlots = useCreateBulkSlotsMutation();
  const deleteSlot = useDeleteAvailabilitySlotMutation();

  const weekStart = useMemo(() => getStartOfWeek(selectedDate), [selectedDate]);
  const weekEnd = useMemo(() => getEndOfWeek(selectedDate), [selectedDate]);
  const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart]);
  const workHours = useMemo(
    () => Array.from({ length: WORK_HOURS_END - WORK_HOURS_START }, (_, i) => i + WORK_HOURS_START),
    []
  );

  const navigateWeek = useCallback(
    (direction: 1 | -1) => {
      const newDate = new Date(selectedDate);
      newDate.setDate(selectedDate.getDate() + direction * DAYS_IN_WEEK);
      onDateChange(newDate);
    },
    [selectedDate, onDateChange]
  );

  const goToPreviousWeek = useCallback(() => navigateWeek(-1), [navigateWeek]);
  const goToNextWeek = useCallback(() => navigateWeek(1), [navigateWeek]);

  const goToToday = useCallback(() => {
    onDateChange(new Date());
  }, [onDateChange]);

  const createSlotFromCoordinates = useCallback(
    (day: number, hour: number): TimeSlot => {
      return {
        day,
        hour,
        date: createDateFromSlot(weekStart, { day, hour, date: new Date() }),
      };
    },
    [weekStart]
  );

  const isSlotValid = useCallback(
    (slot: TimeSlot): boolean => {
      return !isSlotPast(slot, weekStart) && !isSlotBooked(slot, weekStart, availabilitySlots);
    },
    [weekStart, availabilitySlots]
  );

  const handleMouseDown = useCallback(
    (day: number, hour: number) => {
      const slot = createSlotFromCoordinates(day, hour);

      if (!isSlotValid(slot)) {
        return;
      }

      setIsDragging(true);
      setDragStart(slot);
      setSelectedSlots([slot]);
    },
    [createSlotFromCoordinates, isSlotValid]
  );

  const handleMouseEnter = useCallback(
    (day: number, hour: number) => {
      if (!isDragging || !dragStart) return;

      const slot = createSlotFromCoordinates(day, hour);
      const slots = getSlotsInRange(dragStart, slot);
      const validSlots = slots.filter(isSlotValid);

      setSelectedSlots(validSlots);
    },
    [isDragging, dragStart, createSlotFromCoordinates, isSlotValid]
  );

  const resetDragState = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
    setSelectedSlots([]);
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!isDragging || selectedSlots.length === 0) {
      resetDragState();
      return;
    }

    const slotsToCreate = selectedSlots.map((slot) => {
      const start = createDateFromSlot(weekStart, slot);
      const end = new Date(start);
      end.setHours(start.getHours() + 1);

      return {
        start: start.toISOString(),
        end: end.toISOString(),
      };
    });

    createBulkSlots.mutate(
      { userAvailabilitySlots: slotsToCreate },
      {
        onSuccess: resetDragState,
        onError: (error) => {
          resetDragState();
          toast.error(getErrorMessage(error));
        },
      }
    );
  }, [isDragging, selectedSlots, weekStart, createBulkSlots, resetDragState]);

  const handleSlotClick = useCallback(
    (slot: UserAvailabilitySlot) => {
      if (getSlotStatus(slot) !== 'available' && slot.scheduledCall) {
        setSelectedScheduledCall(slot.scheduledCall);
        return;
      }
      if (getSlotStatus(slot) === 'available') {
        deleteSlot.mutate(slot.id);
      }
    },
    [deleteSlot]
  );

  const isSlotSelected = useCallback(
    (day: number, hour: number): boolean => {
      return selectedSlots.some((slot) => slot.day === day && slot.hour === hour);
    },
    [selectedSlots]
  );

  const getSlotClassName = useCallback(
    (day: number, hour: number): string => {
      const slot: TimeSlot = {
        day,
        hour,
        date: createDateFromSlot(weekStart, { day, hour, date: new Date() }),
      };

      const existingSlot = isSlotBooked(slot, weekStart, availabilitySlots);
      const isPast = isSlotPast(slot, weekStart);
      const isSelected = isSlotSelected(day, hour);

      if (isSelected) {
        return cn('border-2 cursor-pointer transition-colors', SLOT_COLORS.selected);
      }

      if (existingSlot) {
        const status = getSlotStatus(existingSlot);

        if (status === 'accepted') {
          return cn('border cursor-not-allowed', SLOT_COLORS.booked);
        }

        if (status === 'pending') {
          return cn('border cursor-not-allowed', SLOT_COLORS.pending);
        }

        if (status === 'rejected') {
          return cn('border cursor-not-allowed', SLOT_COLORS.rejected);
        }

        return cn(
          'border cursor-pointer transition-colors group',
          SLOT_COLORS.available,
          SLOT_HOVER_COLORS.available
        );
      }

      if (isPast) {
        return cn('border cursor-not-allowed', SLOT_COLORS.past);
      }

      return 'border border-neutral-200 hover:bg-neutral-50 cursor-crosshair transition-colors';
    },
    [weekStart, availabilitySlots, isSlotSelected]
  );

  return {
    // State
    selectedScheduledCall,
    setSelectedScheduledCall,
    isDateRangeModalOpen,
    setIsDateRangeModalOpen,
    createBulkSlots,

    // Computed values
    weekStart,
    weekEnd,
    weekDates,
    workHours,

    // Navigation
    goToPreviousWeek,
    goToNextWeek,
    goToToday,

    // Drag handlers
    handleMouseDown,
    handleMouseEnter,
    handleMouseUp,

    // Slot handlers
    handleSlotClick,
    getSlotClassName,
  };
};
